import type {
  AiAssistRequest,
  AiAssistResult,
  LinkIngestionResult,
  SmartThoughtDraft,
  SyncPushOperation,
  SyncPushResponse,
  SyncPullResponse,
  TranscriptionResult,
  VoiceOrganization
} from "../types";

export class ApiError extends Error {
  status: number;
  code?: string;

  constructor(message: string, status = 0, code?: string) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.code = code;
  }
}

async function apiFetch<T>(path: string, init: RequestInit = {}): Promise<T> {
  const headers = new Headers(init.headers);
  if (init.body && !(init.body instanceof FormData)) {
    headers.set("Content-Type", "application/json");
  }
  headers.set("X-Workbench-Client", "web");
  let response: Response;
  try {
    response = await fetch(`/api${path}`, {
      ...init,
      headers,
      credentials: "include",
      signal: init.signal ?? AbortSignal.timeout(20_000)
    });
  } catch (error) {
    throw new ApiError(
      error instanceof DOMException && error.name === "TimeoutError"
        ? "连接超时"
        : "无法连接云端服务",
      0,
      "NETWORK_ERROR"
    );
  }
  const payload = (await response.json().catch(() => ({}))) as {
    error?: string;
    message?: string;
  };
  if (!response.ok) {
    throw new ApiError(
      payload.message ?? payload.error ?? "请求失败",
      response.status,
      payload.error
    );
  }
  return payload as T;
}

export interface AuthStatus {
  authenticated: boolean;
  configured: boolean;
}

export async function getAuthStatus(): Promise<AuthStatus> {
  return apiFetch<AuthStatus>("/auth/me");
}

export async function login(password: string): Promise<void> {
  await apiFetch("/auth/login", {
    method: "POST",
    body: JSON.stringify({ password })
  });
}

export async function logout(): Promise<void> {
  await apiFetch("/auth/logout", { method: "POST", body: "{}" });
}

export async function pullChanges(cursor: number): Promise<SyncPullResponse> {
  return apiFetch<SyncPullResponse>(`/sync/pull?cursor=${cursor}`);
}

export async function pushChanges(operations: SyncPushOperation[]): Promise<SyncPushResponse> {
  return apiFetch<SyncPushResponse>("/sync/push", {
    method: "POST",
    body: JSON.stringify({ operations })
  });
}

export async function requestAi(request: AiAssistRequest): Promise<AiAssistResult> {
  return apiFetch<AiAssistResult>("/ai/assist", {
    method: "POST",
    body: JSON.stringify(request),
    signal: AbortSignal.timeout(50_000)
  });
}

export async function transcribeAudio(
  audio: Blob,
  filename = "voice-note.m4a",
  durationSeconds?: number
): Promise<TranscriptionResult> {
  const form = new FormData();
  form.append("audio", audio, filename);
  if (durationSeconds) form.append("durationSeconds", String(Math.round(durationSeconds)));
  return apiFetch<TranscriptionResult>("/audio/transcribe", {
    method: "POST",
    body: form,
    signal: AbortSignal.timeout(120_000)
  });
}

export async function organizeVoiceTranscript(
  transcript: string,
  source: "recording" | "upload" | "text"
): Promise<VoiceOrganization> {
  return apiFetch<VoiceOrganization>("/voice/organize", {
    method: "POST",
    body: JSON.stringify({ transcript, source }),
    signal: AbortSignal.timeout(60_000)
  });
}

export async function smartCaptureThought(content: string): Promise<SmartThoughtDraft> {
  return apiFetch<SmartThoughtDraft>("/thought/smart-capture", {
    method: "POST",
    body: JSON.stringify({ content }),
    signal: AbortSignal.timeout(60_000)
  });
}

export async function ingestSocialLink(text: string): Promise<LinkIngestionResult> {
  return apiFetch<LinkIngestionResult>("/content/link", {
    method: "POST",
    body: JSON.stringify({ text }),
    signal: AbortSignal.timeout(60_000)
  });
}
